import { useState, useCallback, useMemo } from 'react'

export function useMultiSelect<T extends { id: string }>(items: T[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isSelecting, setIsSelecting] = useState(false)

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(items.map((item) => item.id)))
  }, [items])

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const enterSelectMode = useCallback(() => {
    setIsSelecting(true)
  }, [])

  const exitSelectMode = useCallback(() => {
    setIsSelecting(false)
    setSelectedIds(new Set())
  }, [])

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(item.id)),
    [items, selectedIds],
  )

  return {
    selectedIds,
    selectedItems,
    selectedCount: selectedIds.size,
    isSelecting,
    toggle,
    selectAll,
    deselectAll,
    enterSelectMode,
    exitSelectMode,
    isSelected: (id: string) => selectedIds.has(id),
  }
}
